import {
  identityCreateDealerProxyToken,
  identityDeleteDealerProxyToken,
  identityCreateSocialSession,
  identityCreateUserTokenRefresh,
  type RefreshTokenResponseRoot,
  type SocialLoginCredentialRoot,
} from '@repo/sdk'

import { logger } from './logger'
import { i18n } from '../shared/i18n'

import type { DealerLoginResponse, DealerUser } from '../types/dealer-user'
import type { DealerProxySessionInfo } from './auth-storage'

export type DealerSocialProvider = 'KAKAO' | 'NAVER' | 'GOOGLE' | 'APPLE'

/**
 * Dealer login through the unified SNS login API (`surface: DEALER`, 개발항목 166): a Google·Apple
 * token or a Kakao·Naver code the server exchanges with its own client secret. An identity with no
 * BOLTB account (SIGNUP_REQUIRED — the dealer back-office does not sign people up) or an account
 * without the DEALER role (DEALER_ROLE_REQUIRED, no tokens issued) goes to the dealer-access guide.
 */
export async function loginWithSocial(
  provider: DealerSocialProvider,
  credential: SocialLoginCredentialRoot,
): Promise<DealerLoginResponse> {
  try {
    logger.info('[AuthService] Attempting dealer login:', provider, credential.type)

    const { data } = await identityCreateSocialSession({
      body: { provider, credential, surface: 'DEALER' },
      throwOnError: true,
    })

    const session = data.session
    if (data.result !== 'LOGGED_IN' || !session) {
      throw new Error('DEALER_ROLE_REQUIRED')
    }
    // The server checks the role on this surface; checked again so a wrong response never logs in.
    if (!session.roles.includes('DEALER')) {
      throw new Error('DEALER_ROLE_REQUIRED')
    }

    logger.info('[AuthService] Dealer login successful:', {
      userId: session.userId,
      roles: session.roles,
    })

    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresIn: session.expiresIn,
      user: {
        userId: session.userId,
        nickname: session.nickname,
        profileImage: session.profileImage ?? null,
        roles: session.roles,
        email: session.email ?? null,
      },
    }
  } catch (error_) {
    logger.error('[AuthService] Dealer login failed:', error_)
    throw error_
  }
}

export async function refreshUserToken(refreshToken: string): Promise<RefreshTokenResponseRoot> {
  try {
    logger.info('[AuthService] Attempting token refresh')

    const response = await identityCreateUserTokenRefresh({
      body: { refreshToken },
    })

    if (!response.data) {
      throw new Error(i18n.t('common.errors.tokenRefreshFailed'))
    }

    logger.info('[AuthService] Token refresh successful')

    return response.data
  } catch (error_) {
    logger.error('[AuthService] Token refresh failed:', error_)
    throw error_
  }
}

/** 슈퍼관리자 대행 접속(#273) — 관리자 백오피스가 넘긴 1회용 코드를 이 탭의 접속으로 바꾼다. */
export async function exchangeProxySession(code: string): Promise<{
  accessToken: string
  expiresIn: number
  user: DealerUser
  session: DealerProxySessionInfo
}> {
  const { data } = await identityCreateDealerProxyToken({ body: { code }, throwOnError: true })
  return {
    accessToken: data.accessToken,
    expiresIn: data.expiresIn,
    user: {
      userId: data.userId,
      nickname: data.nickname,
      profileImage: data.profileImage ?? null,
      roles: data.roles,
      email: data.email ?? null,
    },
    session: {
      proxySessionId: data.proxySessionId,
      adminUsername: data.adminUsername,
      expiresAt: data.expiresAt,
    },
  }
}

/** Ends the proxy session on the server; the tab forgets it either way. */
export async function endProxySession(): Promise<void> {
  try {
    await identityDeleteDealerProxyToken({ throwOnError: true })
  } catch (error_) {
    logger.warn('[AuthService] Ending the proxy session failed:', error_)
  }
}
